import { STRUCTURES, type SaveBlob } from "@playnets/data";
import { useUi } from "../store";
import { unlockStructure, writeSave } from "../save";

/**
 * Where jewels go. Winning a level pays jewels; jewels buy permanent access to
 * a defence, forever, on every planet.
 *
 * Two structures are starters at zero — a surface gun and a ring gun — so the
 * board is playable before the player has won anything. Everything after that
 * is a deliberate purchase, which is the point: the reward for a good run is a
 * new thing to build, not a bigger number.
 */
export default function Armoury({
  save,
  onSave,
}: {
  save: SaveBlob;
  onSave: (s: SaveBlob) => void;
}): JSX.Element {
  const planetId = useUi((s) => s.planetId);
  const owned = (id: string): boolean =>
    save.unlockedStructures.includes(id) ||
    (STRUCTURES.find((s) => s.id === id)?.jewelCost ?? 0) === 0;

  const buy = (id: string, cost: number): void => {
    const next = unlockStructure(save, id, cost);
    if (next === save) return;
    onSave(next);
    writeSave(next);
  };

  /**
   * Grouped by where the thing mounts, because that's the decision the player
   * actually makes — a mount is either crust or orbit. Thirteen items in one
   * flat list had no order you'd reason about; four or five under a heading you
   * recognise does.
   *
   * Exclusives for planets you aren't playing are hidden entirely. They were a
   * third of the list and none of them were buyable-and-useful.
   */
  // Merge results aren't for sale — they're what a maxed gun turns into.
  const visible = STRUCTURES.filter(
    (s) => !s.mergeOnly && (!s.exclusiveTo || s.exclusiveTo === planetId),
  );
  const byPrice = (
    a: (typeof STRUCTURES)[number],
    b: (typeof STRUCTURES)[number],
  ): number =>
    Number(owned(a.id)) - Number(owned(b.id)) || a.jewelCost - b.jewelCost;

  const groups: Array<{ title: string; items: typeof visible }> = [
    {
      title: "Crust",
      items: visible
        .filter((s) => s.layer === "surface" || s.layer === "both")
        .sort(byPrice),
    },
    {
      title: "Orbit",
      items: visible
        .filter((s) => s.layer === "ring" || s.layer === "both")
        .sort(byPrice),
    },
  ];

  return (
    <div className="menu">
      <header className="map-head">
        <div>
          <p className="menu-eyebrow">Armoury</p>
          <h1 className="map-title">Defences</h1>
        </div>
        <span className="jewel-chip">
          <span className="jewel" />
          {save.jewels}
        </span>
      </header>

      <div className="shelf">
        {groups.map((group) => (
          <section key={group.title}>
            <p className="shelf-heading">{group.title}</p>
            {group.items.map((s) => {
              const has = owned(s.id);
              const afford = save.jewels >= s.jewelCost;
              return (
                <button
                  key={s.id}
                  className={`shelf-item${has ? " is-owned" : ""}`}
                  disabled={has || !afford}
                  onClick={() => buy(s.id, s.jewelCost)}
                >
                  <span
                    className="shelf-dot"
                    style={{
                      background: `#${s.colour.toString(16).padStart(6, "0")}`,
                    }}
                  />
                  <span className="shelf-body">
                    <span className="card-name">{s.name}</span>
                    <span className="card-text">{s.text}</span>
                  </span>
                  <span className="shelf-price">
                    {has ? (
                      "✓"
                    ) : (
                      <>
                        <span className="jewel jewel--sm" />
                        {s.jewelCost}
                      </>
                    )}
                  </span>
                </button>
              );
            })}
          </section>
        ))}
      </div>

      <button
        className="primary"
        onClick={() => useUi.getState().setScreen("map")}
      >
        Back to map
      </button>
    </div>
  );
}
